import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
	type Control,
	type FieldValues,
	type Path,
	useWatch
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RequiredChip } from '@/components/Chips/RequiredChip';

import { DcrFormController } from '../../DcrFormController/DcrFormController';

import {
	MAX_CHARS,
	reviewModalNotesStyles as sx
} from './ReviewModalNotes.styles';

type Props<T extends FieldValues> = {
	control: Control<T>;
	required?: boolean;
};

export const ReviewModalNotes = <T extends FieldValues>({
	control,
	required = false
}: Props<T>) => {
	const { t } = useTranslation();

	const notesValue = useWatch({ control, name: 'notes' as Path<T> });
	const currentLength = (notesValue || '').length;

	return (
		<Box sx={sx.root}>
			<Box sx={sx.header}>
				<Typography variant='subtitle2' sx={sx.notesLabel}>
					{t('Notes')}:
				</Typography>
				{required && (
					<RequiredChip
						customSx={{
							root: {
								fontWeight: 400,
								marginLeft: 1
							}
						}}
					/>
				)}
			</Box>
			<DcrFormController
				name={'notes' as Path<T>}
				control={control}
				filterable={false}
				render={({ field }) => (
					<TextField
						{...field}
						placeholder={t('Write a note to us.')}
						multiline
						minRows={3}
						variant='standard'
						fullWidth
						slotProps={{
							htmlInput: {
								maxLength: MAX_CHARS,
								sx: sx.notesText
							}
						}}
					/>
				)}
			/>

			<Box sx={sx.limitLabelContainer}>
				<Typography variant='caption' sx={sx.limitLabel(currentLength)}>
					{currentLength}/{MAX_CHARS}
				</Typography>
			</Box>
		</Box>
	);
};
